import React, {useRef} from "react";
import ReactDOM from "react-dom";
import "moment/locale/nl";
import Moment from "moment";
import { extendMoment } from "moment-range";
import { Calendar } from "./components/Calendar";
import { Infobar } from "./components/Infobar";
import { keyBy } from "lodash";
import queryString from "qs";
import Cookies from "js-cookie";
import { Base64 } from "js-base64";
import { reducer, getInitialState } from "./reducer";
import copy from "copy-to-clipboard";
import {YearSwitcher} from "./components/YearSwitcher";


/**
 * The query is used for rendering the first paint of the calendar
 * Get parameters such as "view" and "filters" have effect on the initial view
 * and are updated when a users changes these things.
 */
const parseQuery = () => {
  const query = {
    view: "smallMonth",
    date: false,
    filters: [],
    groups: ["1"],
    ...queryString.parse(window.location.search.replace("?", "")),
  };

  // Convert the filter string (filter id's seperated by a comma) to an array
  if (typeof query.filters == "string") {
    query.filters =
      query.filters === "" ? [] : query.filters.split(".").map((id) => id);
  }

  // Convert the filter groups string (filter id's seperated by a comma) to an array
  if (typeof query.groups == "string") {
    query.groups =
      query.groups === "" ? [] : query.groups.split(".").map((id) => id);
  }

  return query;
};





const app = document.getElementById("app");
const calendar = document.getElementById("calendar");
const infobar = document.getElementById("user-calendar-infobar");
const yearSwitcher = document.getElementById("year-switcher");


const query = parseQuery();

const InfobarPortal = ({ component }) =>
  ReactDOM.createPortal(component, infobar);

const CalendarPortal = ({ component }) =>
  ReactDOM.createPortal(component, calendar);

let YearSwitcherPortal = null;

if(yearSwitcher !== null) {
  YearSwitcherPortal = ({component}) => ReactDOM.createPortal(component, yearSwitcher);
}

export const StateContext = React.createContext();
export const useStateValue = () => React.useContext(StateContext);

const userCalendar =
  calendar !== null && calendar.dataset.userCalendar !== "null"
    ? JSON.parse(calendar.dataset.userCalendar)
    : false;

const defaultChecked = userCalendar
  ? userCalendar.active_filters
  : ["Feestdagen"];

// const filterList =
//   calendar == null
//     ? []
//     : keyBy(
//         [
//           ...JSON.parse(calendar.dataset.filters).map((filter, index) => {
//             return {
//               title: filter.title,
//               eventTypes: filter.types,
//               id: index + 1,
//               featured: filter.featured,
//               checked: defaultChecked.indexOf(filter.title) !== -1
//             };
//           }),
//           {
//             id: 0,
//             title: "Eigen",
//             featured: true,
//             eventTypes: ["0"],
//             checked: false
//           }
//         ],
//         "id"
//       );

const filterList =
  calendar == null
    ? []
    : keyBy(
        [
          ...JSON.parse(calendar.dataset.filters),
          {
            title: "Eigen items",
            region: ["none"],
            group: "own",
            id: "0",
          },
        ],
        "id"
      );

// const filterGroups = {
//   own: {
//     id: "0",
//     title: "Eigen"
//   },
//   holidays: {
//     id: "1",
//     title: "Feestdagen"
//   },
//   school_holidays_north: {
//     id: "2",
//     title: "Schoolvakantie Noord"
//   },
//   school_holidays_mid: {
//     id: "3",
//     title: "Schoolvakantie Midden"
//   },
//   school_holidays_south: {
//     id: "4",
//     title: "Schoolvakantie Zuid"
//   }
// };

const filterGroups = [
  {
    group: "own",
    id: "0",
    region: false,
    title: "Eigen items",
  },
  {
    group: "holidays",
    id: "1",
    region: false,
    title: "Feestdagen",
  },
  {
    region: "north",
    group: "school_holidays",
    id: "2",
    title: "Schoolvakantie Noord",
  },
  {
    region: "mid",
    group: "school_holidays",
    id: "3",
    title: "Schoolvakantie Midden",
  },
  {
    region: "south",
    group: "school_holidays",
    id: "4",
    title: "Schoolvakantie Zuid",
  },
];

// This enables moment().range()
// https://github.com/rotaready/moment-range
const moment = extendMoment(Moment);

// Set the locale for moment
moment.locale("nl");

// Helper function for adding a range object when the startdate and enddate
// of the event are not equal
export const addRangeToEvents = (events) => {
  return events.map((d) => {
    d.range =
      d.start === d.end
        ? false
        : moment().range(moment(d.start), moment(d.end));

    return d;
  });
};

/**
 * Based on the activated filters get a list of filtered events
 *
 * There can be a relationship between an event and an eventType, there is an array with relationships
 * available from the backend
 *
 * There is also a list of filters, these filters are a compound of one or multiple eventTypes.
 * The activeFilters array in this function is al list of id's from these filters.
 *
 * @param {array} events List of all events [...events, ...userEvents]
 * @param {array} activeFilters List of id's of currently active filters
 */
export const getFilteredEvents = (
  events,
  { activeFilterGroups, activeFilters, activeSubtractions }
) => {
  // If no filters are selected return no events
  if (activeFilters?.length === 0 && activeFilterGroups?.length === 0) return [];

  const filteredEvents = [];

  const filtersFromGroups = [];

  Object.keys(filterList).forEach((i) => {
    const filter = filterList[i];

    const inGroup = filterGroups.findIndex((group) => {
      if (
        group.group === filter.group &&
        activeFilterGroups.indexOf(group.id) !== -1
      ) {
        if (!group.region || filter.region.indexOf(group.region) !== -1) {
          return true;
        }
        return false;
      }

      return false;
    });

    if (inGroup !== -1) {
      filtersFromGroups.push(filter.id);
    }
  });

  events.forEach((event) => {

    if (
      activeFilters.indexOf(event.filter_id) !== -1 &&
      activeSubtractions.indexOf(event.filter_id) === -1
    ) {
      filteredEvents.push(event);

    } else if (
      filtersFromGroups.indexOf(event.filter_id) !== -1 &&
      activeSubtractions.indexOf(event.filter_id) === -1
    ) {
      filteredEvents.push(event);

    } else if (
      event.created_by !== null &&
      (activeFilters.indexOf("0") !== -1 ||
        activeFilterGroups.indexOf("0") !== -1)
    ) {
      filteredEvents.push(event);

      if(event.repeat !== null && event.repeat !== '') {
        if(event.repeat === 'daily') {
            for(let i = 1 ; i < event.repeat_duration; i++) {
              let e = Object.assign({}, event);
              e.start = moment(e.start).add(i, 'day').format('YYYYMMDD');
              e.end = moment(e.end).add(i, 'day').format('YYYYMMDD');
              filteredEvents.push(e);
            }

        }
        if(event.repeat === 'weekly') {
            for(let i = 1 ; i < event.repeat_duration; i++) {
                let e = Object.assign({}, event);
                e.start = moment(e.start).add(i, 'week').format('YYYYMMDD');
                e.end = moment(e.end).add(i, 'week').format('YYYYMMDD');
                filteredEvents.push(e);
            }
        }
        if(event.repeat === 'monthly') {
            for(let i = 1 ; i < event.repeat_duration; i++) {
                let e = Object.assign({}, event);
                e.start = moment(e.start).add(i, 'month').format('YYYYMMDD');
                e.end = moment(e.end).add(i, 'month').format('YYYYMMDD');
                filteredEvents.push(e);
            }
        }
        if(event.repeat === 'yearly') {
            for(let i = 1 ; i < event.repeat_duration; i++) {
                let e = Object.assign({}, event);
                e.start = moment(e.start).add(i, 'year').format('YYYYMMDD');
                e.end = moment(e.end).add(i, 'year').format('YYYYMMDD');
                filteredEvents.push(e);
            }
        }

      }
    }
  });

  return filteredEvents;
};

export const addUserCalendarToCookies = ({ uid, year }) => {
  // Get existing cookie or empty array
  const userCalendars =
    typeof Cookies.get("user_calendars") === "undefined"
      ? []
      : JSON.parse(Base64.decode(Cookies.get("user_calendars")));

  // Check if the calendar we wat to create is already inside the cookies
  const calendarExists =
    userCalendars.findIndex((calendar) => calendar.uid == uid) !== -1;

  // Push a new calendar in the array if the calendar doesn't exits
  if (!calendarExists) {
    userCalendars.push({ uid: uid, year: year, added: new Date() });

    // Update the cookie
   /* Cookies.set(
      "user_calendars",
      Base64.encode(JSON.stringify(userCalendars)),
      {
        expires: 365,
      }
    );*/
  }
};

export const getUserCalendarFromCookies = () => {
  return typeof updatedUserCalendarCookie !== "undefined"
    ? JSON.parse(Base64.decode(updatedUserCalendarCookie))
    : typeof Cookies.get("user_calendars") === "undefined"
    ? []
    : JSON.parse(Base64.decode(Cookies.get("user_calendars")));
};

/**
 * App
 */
const AppWithContext = () => {
  const [{ year }, dispatch] = useStateValue();
  const [creatingCalendar, setCreatingCalendar] = React.useState(false);
  const [createCalendarFlag, setCreateCalendarFlag] = React.useState(false);
  const [calendarUid, setCalendarUid] = React.useState(false);
  const [calendarEUid, setCalendarEUid] = React.useState(false);
  const [calendarName, setCalendarName] = React.useState(false);

  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const emailHolderRef = useRef(null);
  const emailInfoRef = useRef(null);

  const [isCopied, setIsCopied] = React.useState(false);

  const handleCopy = (url, event) => {
    copy(url);
    event.currentTarget.classList.add('copied');
    setIsCopied(true);
  };
  const afterCalendarCreation = ({ uid, year }) => {
    dispatch({ type: "updateUid", payload: uid });
  };

  const handleSetEmail = () => {

        const body = queryString.stringify({
        nonce: wp.nonce,
        action: "email_custom_calendar",
        email: emailInputRef.current.value,
        uid: calendarUid,
        });

        // Fetch yo
        fetch(wp.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: new Headers({
            "Content-Type": "application/x-www-form-urlencoded",
        }),
        body: body,
        })
            .then((res) => res.json())
            .then((res) => {
            if (res.email) {
                emailHolderRef.current.classList.add("hidden");
                emailInfoRef.current.classList.remove("hidden");
            }
            })
            .catch((err) => {
            setCreatingCalendar(false);
            console.warn(err);
            });
  }
  const handleSetName = () => {

    const body = queryString.stringify({
      nonce: wp.nonce,
      action: "name_custom_calendar",
      name: nameInputRef.current.value,
      uid: calendarUid,
    });

    // Fetch yo
    fetch(wp.ajaxUrl, {
      method: "POST",
      credentials: "same-origin",
      headers: new Headers({
        "Content-Type": "application/x-www-form-urlencoded",
      }),
      body: body,
    })
        .then((res) => res.json())
        .then((res) => {
          if (res.name) {
            setCalendarName(res.name);
          }
        })
        .catch((err) => {
          setCreatingCalendar(false);
          console.warn(err);
        });
  };

  const handleCreateCalendar = () => {
      if(window.location.pathname.indexOf('maak-kalender') == -1)
      {
          window.location.href = '/maak-kalender/';
          return;
      }
    setCreatingCalendar(true);

    // let elements = document.getElementsByClassName('container');
    //
    // let found = false;
    //
    // for(let i = 0; i < elements.length -1; i++) {
    //   if(elements[i].classList.contains('headerad') ) {
    //     elements[i].classList.add('hidden');
    //     found = true;
    //   } else if(found) {
    //     elements[i].classList.add('hidden');
    //   }
    // }
    //
    // elements = document.getElementsByClassName('sideads');
    //
    // for(let i = 0; i < elements.length -1; i++) {
    //     elements[i].classList.add('hidden');
    // }

    /*if (calendar !== null) {
      calendar = null;
      //setCreateCalendarFlag(true);
      //return;
    }*/

    const year = moment().format("YYYY");

    const body = queryString.stringify({
      nonce: wp.nonce,
      action: "create_custom_calendar",
      year: year,
      active_filter_groups: '["1", "0"]',
      active_filters: "[]",
      active_view: "smallMonth",
    });

    // Fetch yo
    fetch(wp.ajaxUrl, {
      method: "POST",
      credentials: "same-origin",
      headers: new Headers({
        "Content-Type": "application/x-www-form-urlencoded",
      }),
      body: body,
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.uid) {
          // Add it to cookies (because we don't have a login system)
          //addUserCalendarToCookies({ uid: res.uid, year: year });
          setCalendarUid(res.uid);
          setCalendarEUid(res.euid);
          //const newUrl = `${window.location.origin}/agenda/bekijk/${res.uid}?created=true`;
          //window.location = newUrl;
        }
      })
      .catch((err) => {
        setCreatingCalendar(false);
        console.warn(err);
      });
  };

  React.useMemo(() => {
    if (typeof updatedUserCalendarCookie !== "undefined") {
      Cookies.set("user_calendars", updatedUserCalendarCookie, {
        expires: 365,
      });
    }
  }, []);


  const setYear = (year) => {
    dispatch({ type: "updateYear", payload: year });
  }

  return (
    <>
      {creatingCalendar && calendarUid == false && (
        <div className="creatingCalendarPopup" style={{ opacity: 1 }}>
          <iframe
            frameBorder={0}
            scrolling="no"
            src="/wp-content/themes/jaarkalender/parts/calendar-loader.php"
          />
          <p>We zetten nu je eigen kalender voor je klaar.</p>
        </div>
      )}
      {creatingCalendar && calendarUid != false && calendarName === false && (
          <div className="infoCalendarPopup" style={{ opacity: 1 }}>
            <div className="indicator">
              <div className="indicator__bar">
                <span className="number active">1</span>
                <span className="line"></span>
                <span className="number">2</span>
              </div>
            </div>
            <h1>Geef je kalender een naam</h1>
            <div className="buttonBlock"><input type="text" name="name" placeholder="Mijn kalender" ref={nameInputRef} /><a onClick={handleSetName} href="#">Maak kalender</a></div>
          </div>
      )}
      {creatingCalendar && calendarUid != false && calendarName !== false && (
          <div className="infoCalendarPopup" style={{ opacity: 1 }}>
            <div className="indicator">
              <div className="indicator__bar">
                <span className="number completed">1</span>
                <span className="line"></span>
                <span className="number active">2</span>
              </div>
            </div>
            <h1>Hoera, je kalender is aangemaakt! &#x1F973;</h1>
            <p className="greenInfo">Via onderstaande links heb je toegang tot je kalender. <b>Zorg ervoor dat je deze goed bewaard.</b><br/>Vul hieronder je e-mail in om deze linkjes ook per e-mail te ontvangen. (Optioneel)</p>
            <div className="linkBlock"><span className="linkTitle">Link voor de beheerder(s)</span>
              <div className="linkHolder"><span className="linkUrl">{window.location.origin}/?agenda={encodeURIComponent(calendarName)}&bewerk={calendarUid}</span><button
                className={`btn btn--infobarCopy`}
                onClick={(event) => handleCopy(window.location.origin + '/?agenda=' + encodeURIComponent(calendarName) + '&bewerk=' + calendarUid, event)}
            >
                    <span>Copy</span>
              </button></div><span className="linkFooter"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15">
  <path id="circle-info-solid" d="M7.5,15A7.5,7.5,0,1,0,0,7.5,7.5,7.5,0,0,0,7.5,15ZM6.328,9.844h.7V7.969h-.7a.7.7,0,0,1,0-1.406H7.734a.7.7,0,0,1,.7.7V9.844h.234a.7.7,0,0,1,0,1.406H6.328a.7.7,0,0,1,0-1.406ZM7.5,5.625a.938.938,0,1,1,.938-.937A.936.936,0,0,1,7.5,5.625Z" fill="#0069f4"/>
</svg>Deel deze link met <b>beheerders.</b> Beheerders kunnen wijzigingen aanbrengen.</span> </div>
            <div className="linkBlock"><span className="linkTitle">Openbare link</span><div className="linkHolder"><span className="linkUrl">{window.location.origin}/?agenda={encodeURIComponent(calendarName)}&bekijk={calendarEUid}</span><button
                className={`btn btn--infobarCopy`}
                onClick={(event) => handleCopy(window.location.origin + '/?agenda=' + encodeURIComponent(calendarName) + '&bekijk=' + calendarEUid, event)}
            >
              <span>Copy</span>
            </button></div><span className="linkFooter"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15">
  <path id="circle-info-solid" d="M7.5,15A7.5,7.5,0,1,0,0,7.5,7.5,7.5,0,0,0,7.5,15ZM6.328,9.844h.7V7.969h-.7a.7.7,0,0,1,0-1.406H7.734a.7.7,0,0,1,.7.7V9.844h.234a.7.7,0,0,1,0,1.406H6.328a.7.7,0,0,1,0-1.406ZM7.5,5.625a.938.938,0,1,1,.938-.937A.936.936,0,0,1,7.5,5.625Z" fill="#0069f4"/>
</svg>Deel deze link met <b>leden.</b> Leden kunnen geen wijzigingen aanbrengen.</span> </div>
            <p className="tipInfo">Verstuur je kalenderlinks per e-mail. (Optioneel)</p>
            <div className="buttonBlock" ref={emailHolderRef}><div className="inputHolder"><input type="text" name="email" placeholder="Je e-mailadres" ref={emailInputRef} /><button onClick={handleSetEmail} className={"grey"}>Verstuur e-mail</button></div></div>
            <p className="greenInfo hidden" ref={emailInfoRef}>Je ontvangt een e-mail met de linkjes naar je kalender.</p>
            <div className="buttonBlock"><a href={window.location.origin + '/?agenda=' + encodeURIComponent(calendarName) + '&bewerk=' + calendarUid}>Naar je kalender</a></div>
          </div>
      )}
      {creatingCalendar || calendar === null ? null : (
        <CalendarPortal
          component={
            <Calendar
              filterList={filterList}
              moment={moment}
              afterCalendarCreation={afterCalendarCreation}
              userCalendar={userCalendar}
              filterGroups={filterGroups}
              createCalendarFlag={createCalendarFlag}
              setCreatingCalendar={setCreatingCalendar}
              currentYear={year}
            />
          }
        />
      )}
      <InfobarPortal
        component={
          <Infobar
            currentYear={year}
            uid={userCalendar}
            app={app}
            createCalendar={() => handleCreateCalendar()}
          />
        }
      />
      {yearSwitcher !== null && (
        <YearSwitcherPortal component={<YearSwitcher year={year} yearFunction={setYear} />} />
          )}
    </>
  );
};


const App = () => {
  return (
    <StateContext.Provider
      value={React.useReducer(
        reducer,
        getInitialState({ calendar, userCalendar, query, moment })
      )}
    >
      <AppWithContext />
    </StateContext.Provider>
  );
};

ReactDOM.render(<App />, app);
